import { create } from 'zustand';
import type { LimitErrorMeta, PaywallPayload } from '@/shared/lib/api';
import type { LimitPaywallCode } from '@/shared/stores/limit-paywall-store';

/**
 * Which Pass the paywall opens highlighted. Both remaining products are
 * sellable: the Tokens Pro Pass and the Pro Plan subscription, so each
 * highlight opens the actual Pass sheet. (Designer Pass was fully removed —
 * it collapsed into the Pro Pass.)
 */
export type PassPaywallHighlight = 'pro' | 'pro_plan';

/** The token-paywall dialog state to restore when the Pass sheet is backed
 *  out of via `returnTo` — mirrors `LimitPaywallState`'s open payload. */
export interface PassPaywallReturnTo {
  payload: PaywallPayload;
  code: LimitPaywallCode;
  meta: LimitErrorMeta | null;
}

/**
 * App-wide presenter for the Pass paywall dialog.
 *
 * Mirrors `useLimitPaywallStore` + Apple's `PassPaywallPresenter`: the dialog
 * is mounted once at the root and any deep gate just calls `open(highlight)`.
 *
 * The Tokens Pro Pass and Pro Plan are both on sale — a locked-feature tap
 * opens the Pass sheet highlighting the requested tier. `GET /passes` serves
 * the sellable catalog.
 */
interface PassPaywallState {
  highlight: PassPaywallHighlight | null;
  /**
   * Set when this sheet was opened FROM the token-paywall dialog (the
   * "Pro Plan" alternative-action card). The token dialog closes itself
   * before opening this one (two stacked modal Dialogs double the overlay
   * dim — see LimitPaywallDialog), so the back button here restores it via
   * `useLimitPaywallStore.showPaywall(...)` instead of just closing.
   */
  returnTo: PassPaywallReturnTo | null;
  open: (highlight: PassPaywallHighlight, returnTo?: PassPaywallReturnTo) => void;
  close: () => void;
}

export const usePassPaywallStore = create<PassPaywallState>((set) => ({
  highlight: null,
  returnTo: null,
  open: (highlight, returnTo) => set({ highlight, returnTo: returnTo ?? null }),
  close: () => set({ highlight: null, returnTo: null }),
}));
